import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useI18n } from "@wato/i18n/react";
import { Button } from "@wato/ui";
import { useApi } from "../../../api/context";
import { mayUse } from "../../roles/role-label";
import { useWorkspace } from "../../workspace/WorkspaceContext";
import { computeReadiness, type ReadinessInputs } from "./compute-readiness";

const dismissKey = (workspaceId: string) => `wato.settings.readinessDismissed.${workspaceId}`;

function readDismissed(workspaceId: string): boolean {
  try {
    return localStorage.getItem(dismissKey(workspaceId)) === "1";
  } catch {
    return false; // storage blocked: just show the bar
  }
}

/** OWN-365: loads the five inputs from existing endpoints; a failed one counts as "not done". */
function useReadinessInputs(): ReadinessInputs | null {
  const api = useApi();
  const { workspace } = useWorkspace();
  const [inputs, setInputs] = useState<ReadinessInputs | null>(null);
  useEffect(() => {
    let live = true;
    const settle = async <T,>(load: () => Promise<T>): Promise<T | null> => {
      try {
        return await load();
      } catch {
        return null; // a failed input counts as "not done"
      }
    };
    void Promise.all([
      settle(() => api.getBusinessHours(workspace.id)),
      settle(() => api.listChannels(workspace.id)),
      settle(() => api.getIntegrationsHub(workspace.id)),
      // Q4-ONB-08: a role without team.view never loads the member list.
      settle(() =>
        mayUse(workspace, "team.view") ? api.listMembers(workspace.id) : Promise.resolve(null),
      ),
    ]).then(([hours, channels, hub, members]) => {
      if (!live) return;
      setInputs({
        hasLogo: workspace.logo !== null && workspace.logo !== undefined,
        hours,
        channels,
        googleConnected:
          hub?.google.sheets.status === "connected" || hub?.google.docs.status === "connected",
        members,
      });
    });
    return () => {
      live = false;
    };
  }, [api, workspace, workspace.id, workspace.logo]);
  return inputs;
}

/** Top-of-settings completeness bar; hidden at 100% and when dismissed (per browser). */
export function SettingsReadiness() {
  const { t, locale } = useI18n();
  const { workspace } = useWorkspace();
  const inputs = useReadinessInputs();
  const [dismissed, setDismissed] = useState(() => readDismissed(workspace.id));
  if (!inputs || dismissed) return null;
  const { percent, items } = computeReadiness(inputs);
  if (percent >= 100) return null;
  const percentText = new Intl.NumberFormat(locale, { style: "percent" }).format(percent / 100);
  const dismiss = () => {
    try {
      localStorage.setItem(dismissKey(workspace.id), "1");
    } catch {
      /* storage blocked: dismissed for this visit only */
    }
    setDismissed(true);
  };
  return (
    <section
      className="mb-3 flex flex-col gap-2 rounded-lg bg-surface p-3 shadow-sm"
      data-testid="settings-readiness"
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold">
          {t("settingsReadiness.b29b.title", { percent: percentText })}
        </p>
        <Button
          size="sm"
          variant="ghost"
          onClick={dismiss}
          data-testid="settings-readiness-dismiss"
        >
          {t("settingsReadiness.b29b.dismiss")}
        </Button>
      </div>
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        aria-label={t("settingsReadiness.b29b.title", { percent: percentText })}
        className="h-1.5 overflow-hidden rounded-full bg-line"
      >
        <div className="h-full bg-action-primary" style={{ inlineSize: `${percent}%` }} />
      </div>
      <ul className="flex flex-wrap gap-2">
        {items
          .filter((i) => !i.done)
          .map((i) => (
            <li key={i.id}>
              <Link
                to={`/w/${workspace.id}/${i.href}`}
                data-testid={`settings-readiness-${i.id}`}
                className="inline-block rounded-md bg-action-ghost-hover px-2 py-1 text-xs text-on-action-ghost"
              >
                {t(`settingsReadiness.b29b.items.${i.id}` as never)}
              </Link>
            </li>
          ))}
      </ul>
    </section>
  );
}
